import { useMutation } from "@tanstack/react-query";
import { useUcService } from "./useUcService";
import axios from "axios";
import address from "@/apis/address";
import { notifyWarning } from "@/utils/notification";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { useContractRecordQuery } from "@/pages/contractManagement/hooks/useContractRecordQuery";

export type ContractCheckResult = {
  id: number;
  /** 检测状态：0-检测中，1-检测完成，2-检测失败 */
  detectionStatus: number;
  /** 检测结果 */
  detectionResult: string | null;
  /** pdf转换后的图片url列表 */
  previewLink: string | null;
};

export function useCheckResultMutation() {
  const token = useUcService();

  const checkResultMutation = useMutation({
    mutationFn: (contractUrl: string) =>
      axios.get<{
        msg: string;
        code: number;
        data: null | ContractCheckResult;
      }>("/investigate/invDocument/srm/get", {
        params: {
          url: contractUrl,
          // url: encodeURIComponent(
          //   "https://winwaywebtest.tos-cn-guangzhou.volces.com/AI中台-内容合规审查/澳美中药厂年保服务合同(2025-2026)-New_1767603155999.pdf",
          // ),
        },
        baseURL: address.ai,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
  });

  return checkResultMutation;
}

export function useUploadResultMutation() {
  const token = useUcService();

  const uploadResultMutation = useMutation({
    mutationFn: (contractUrl: string) =>
      axios.post<{
        msg: string;
        code: number;
        data: null | ContractCheckResult;
      }>(
        "/investigate/invDocument/addBatch",
        {
          documentType: 1,
          contractStance: 1,
          status: 2,
          fileList: [
            {
              documentUrl: contractUrl,
              documentUrlType: 1,
            },
          ],
        },
        {
          baseURL: address.ai,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      ),
  });

  return uploadResultMutation;
}

export function useCheckContract({ openModal }: { openModal: () => void }) {
  const { t } = useTranslation("contractManagement");
  const [checkResult, setCheckResult] = useState<ContractCheckResult | null>(
    null,
  );
  const [recordId, setRecordId] = useState<number | null>(null);

  /**
   * hasChecked: 防止无限循环的关键标志位
   *
   * 无限循环原因分析：
   * 1. useMutation 返回的对象引用每次渲染都会变化
   * 2. mutate() 被调用后，React Query 内部状态变化会触发组件重渲染
   * 3. 重渲染 → 新的 mutation 对象引用 → useCallback 重新创建 → useEffect 再次执行
   * 4. useEffect 再次调用 mutate() → 无限循环
   *
   * 解决方案：
   * hasChecked 标志位确保 handleCheckResult 只会执行一次，
   * 直到 recordId 变化或 cleanUp() 被调用才会重置
   */
  const [hasChecked, setHasChecked] = useState(false);

  const uploadResultMutation = useUploadResultMutation();
  const checkResultMutation = useCheckResultMutation();

  const detail = useContractRecordQuery(recordId);

  const srmSupplierFlowBusinessFiles = detail?.srmSupplierFlowBusinessFiles;

  const contractFiles = srmSupplierFlowBusinessFiles?.find(
    (file) => file.fileType === "contractFiles",
  );
  const signedContracts = srmSupplierFlowBusinessFiles?.find(
    (file) => file.fileType === "signedContracts",
  );

  const contractFileUrl = contractFiles?.fileUrl;
  const signedContractFileUrl = signedContracts?.fileUrl;

  /**
   * 触发合同检测
   * - 设置 recordId 触发数据查询
   * - 重置 hasChecked 确保检测逻辑能正常执行
   */
  const handleCheck = (id: number) => {
    setRecordId(id);
    setHasChecked(false);
  };

  /** 重置所有状态 */
  const cleanUp = () => {
    setRecordId(null);
    setCheckResult(null);
    setHasChecked(false);
  };

  /**
   * 执行合同检测的核心逻辑
   *
   * 检测流程：
   * 1. 前置检查：recordId、detail 存在，且未进行过检测
   * 2. 获取合同文件 URL（优先使用已签章合同）
   * 3. 调用检测接口
   * 4. 根据检测结果处理：
   *    - 无数据 → 调用上传接口提交合同
   *    - 检测中（无 detectionResult）→ 提示等待
   *    - 检测完成 → 设置结果并打开弹窗
   */
  const handleCheckResult = useCallback(() => {
    // 前置检查：确保只执行一次
    if (!recordId || !detail || hasChecked) return;

    let checkUrl = contractFileUrl;

    // 无合同文件时，提示并标记已检查（防止重复提示）
    if (!checkUrl) {
      setHasChecked(true);
      return notifyWarning(t("未上传合同：查询不到目标文件，请先上传合同"));
    }

    // 优先使用已签章合同
    if (signedContractFileUrl) {
      checkUrl = signedContractFileUrl;
    }

    // 标记为已检查，防止 useEffect 重复触发
    setHasChecked(true);

    // 调用检测接口
    checkResultMutation.mutate(checkUrl, {
      onSuccess: (data) => {
        console.log({ data });

        // code === 0 表示接口调用成功
        if (data.data.code === 0) {
          // data.data.data 为 null 表示该合同尚未提交检测，需要先上传
          if (!data.data.data) {
            uploadResultMutation.mutate(checkUrl!, {
              onSuccess(data) {
                if (data.data.code === 0) {
                  notifyWarning(t("合同正在审核，请耐心等待"));
                } else {
                  notifyWarning(data.data?.msg || t("合同审核失败"));
                }
              },
            });
            return;
          }

          // 有数据但检测结果为空，表示正在检测中
          if (data.data.data.id && !data.data.data.detectionResult) {
            return notifyWarning(t("合同正在审核，请耐心等待"));
          }

          // 检测完成，设置结果并打开弹窗
          setCheckResult(data.data.data);
          openModal();
        } else {
          // 接口返回错误
          notifyWarning(data.data?.msg || t("合同审核失败"));
        }
      },
    });
  }, [
    recordId,
    detail,
    hasChecked,
    checkResultMutation,
    uploadResultMutation,
    openModal,
    t,
    contractFileUrl,
    signedContractFileUrl,
  ]);

  // 监听 handleCheckResult 变化时执行检测
  useEffect(() => {
    handleCheckResult();
  }, [handleCheckResult]);

  return {
    checkResult,
    handleCheck,
    cleanUp,
  };
}
